<template lang="pug">
div(
    class="tl-datepicker"
    :class="{ dense: dense }"
    data-form-validator
)
    Datepicker(
        ref="datePicker"
        v-model="modelState"
        calendar-class-name="tl-datepicker-calendar"
        menu-class-name="tl-datepicker-calendar-menu"
        text-input
        :model-type="datePickerFormat"
        :enable-time-picker="isDateTimePicker"
        :disabled-week-days="disabledWeekDays"
        :range="range"
        :min-date="min"
        :max-date="max"
        :auto-apply="!isDateTimePicker && !range"
        :utc="utc ? 'preserve' : false"
        :teleport-center="teleportCenter"
        @open="isFocused = true"
        @closed="isFocused = false"
    )
        template(#arrow-left)
            i(class="mi-chevron-left")

        template(#arrow-right)
            i(class="mi-chevron-right")

        template(#trigger)
            div(
                :class="{ 'animation-shake': error }"
                class="input-wrapper"
            )
                div(
                    :class="{ error: error, focus: isFocused, smallLabel: !modelStateEmpty && !!label, disabled: disabled, opaq: opaq }"
                    class="label-input-wrapper full-width"
                    tabindex="0"
                )
                    div
                        tl-position(
                            class="label fill-height"
                            x="stretch"
                            y="center"
                        )
                            div  {{ label }}

                        transition(
                            name="fade-1"
                        )
                            div(
                                v-if="modelState"
                                class="value"
                            )
                                span(class="text-4 text-line-clamp line-clamp-1") {{ modelTextDisplay }}

                    div(class="tl-datepicker-calendar-trigger")
                        i(:class="{ 'mi-chevron-down': !isFocused, 'mi-chevron-up': isFocused}")

        template(
            v-if="showShortcuts"
            #left-sidebar
        )
            ul(
                class="tl-datepicker-shortcuts"
            )
                li(
                    v-for="shortcut in shortcuts"
                    :key="shortcut.value"
                    :data-testid="`${shortcut.value}`"
                    @click="handleShortcutClick(shortcut.value)"
                ) {{ shortcut.label }}

            p(
                class="tl-datepicker-reset"
                @click="resetValue"
            ) Reset

        template(#action-row="{ internalModelValue, selectDate }")
            tl-button(
                class="action-button"
                :secondary="true"
                :dense="true"
                :disabled="autoApplyDisabled(internalModelValue)"
                @click="selectDate"
            ) {{ applyLabel }}

        template(
            v-if="!showShortcuts"
            #action-extra
        )
            p(
                class="tl-datepicker-reset"
                @click="resetValue"
            ) Reset
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import utcFormat from 'dayjs/plugin/utc'
import equals from 'fast-deep-equal/es6'

export type InputDatePickerModel = string | [string, string];

export interface InputDatePickerProps {
    modelValue: InputDatePickerModel
    format?: string
    displayFormat?: string
    min?: string
    max?: string
    error?: string | boolean
    disabled?: boolean
    label?: string
    emptyStateLabel?: string
    opaq?: boolean
    dense?: boolean
    applyLabel?: string
    disableWeekends?: boolean
    showShortcuts?: boolean
    range?: boolean
    utc?: boolean
    teleportCenter: boolean
}

dayjs.extend(customParseFormat)
dayjs.extend(quarterOfYear)
dayjs.extend(utcFormat)

const props = withDefaults(defineProps<{
    modelValue: InputDatePickerModel
    format?: string
    displayFormat?: string
    min?: string
    max?: string
    error?: string | boolean
    disabled?: boolean
    label?: string
    emptyStateLabel?: string
    opaq?: boolean
    dense?: boolean
    applyLabel?: string
    disableWeekends?: boolean
    showShortcuts?: boolean
    range?: boolean
    utc?: boolean
    teleportCenter?: boolean
}>(), {
    format: 'YYYY-MM-DD',
    showShortcuts: false,
    range: false,
    utc: false,
    applyLabel: 'Apply',
    teleportCenter: false,
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: InputDatePickerModel): void
}>()

const isFocused = ref<boolean>(false)
const modelState = ref<InputDatePickerModel | undefined>(props.modelValue)
const modelValue = computed(() => props.modelValue)
const modelStateEmpty = computed(() => {
    const model = modelState.value
    if (props.range && Array.isArray(model)) {
        return !model.some(d => d)
    }
    return !model
})
const displayFormat = computed<string>(() => {
    if (props.utc) {
        return ''
    }
    if (props.displayFormat) {
        return adjustFormatToDayjs(props.displayFormat)
    }

    return adjustFormatToDayjs(
        isDateTimePicker.value ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY',
    )
})
const modelTextDisplay = computed(() => {
    const model = modelState.value
    if (props.range && Array.isArray(model)) {
        if (modelStateEmpty.value) {
            return props.emptyStateLabel
        }
        const [start, end] = model.map(d => dayjs(d))
        if (start.isSame(end, 'day')) {
            return dayjs(start).format(displayFormat.value)
        }

        return [
            start.format(displayFormat.value),
            end.format(displayFormat.value),
        ].join(' to ')
    }
    const date = dayjs(model as string)
    return (props.utc ? date.utc() : date).format(displayFormat.value)
})

const adjustFormatToDayjs = (format: string) => format.replace(/y/g, 'Y')
    .replace(/d/g, 'D')
    .replace(/h/g, 'H')

const dayjsFormat = computed<string>(() => adjustFormatToDayjs(props.format))

const datePickerFormat = computed<string>(() => {
    const adjustedFormat = dayjsFormat.value.toLowerCase()
        .replace(/([mh])/g, (_, g) => g.toUpperCase())
        .replace(/MM$/, 'mm')

    return adjustedFormat
})

const datePicker = ref<InstanceType<typeof Datepicker>>()

const isDateTimePicker = computed(() => props.utc || /[Hh]/.test(dayjsFormat.value))

const formatDate = (d: string | dayjs.Dayjs): string => {
    const date = dayjs(d)

    if (!date.isValid()) {
        return ''
    }

    if (props.utc) {
        return date.utcOffset(0).format()
    }
    return date.format(dayjsFormat.value)
}

const isBetweenMinMax = (d: InputDatePickerModel | Date | Date[]) => {
    if (Array.isArray(d)) return

    const currentDate = dayjs(d)

    if (props.min) {
        const min = dayjs(props.min, dayjsFormat.value)
        if (currentDate.isBefore(min)) {
            return false
        }
    }

    if (props.max) {
        const max = dayjs(props.max, dayjsFormat.value)
        if (currentDate.isAfter(max)) {
            return false
        }
    }

    return true
}

const disabledWeekDays = computed(() => props.disableWeekends
    ? [0, 6]
    : [],
)

const autoApplyDisabled = (internalModelValue: Date | Date[] | null) => {
    if (props.range) {
        return !(Array.isArray(internalModelValue) && internalModelValue.length === 2)
    }

    if (isDateTimePicker.value) {
        return !internalModelValue || !isBetweenMinMax(internalModelValue)
    }
    return false
}

type ShortcutItem = {
    label: string
    value: 'today' | 'week' | 'month' | 'quarter' | 'year'
}
const shortcuts: ShortcutItem[] = [
    {
        label: 'Today',
        value: 'today',
    },
    {
        label: 'Last Week',
        value: 'week',
    },
    {
        label: 'Last Month',
        value: 'month',
    },
    {
        label: 'Last Quarter',
        value: 'quarter',
    },
    {
        label: 'Last Year',
        value: 'year',
    },
]

const resetValue = () => {
    if (Array.isArray(modelState.value)) {
        modelState.value = ['', '']
    } else {
        modelState.value = ''
    }
    if (datePicker.value) {
        datePicker.value.closeMenu()
    }
}

const handleShortcutClick = (type: ShortcutItem['value']) => {
    if (!datePicker.value || !props.range) return

    const today = dayjs()
    const end = type === 'today'
        ? today
        : today.clone().add(-1, type as dayjs.ManipulateType)
    modelState.value = [formatDate(end), formatDate(today)]
    datePicker.value.closeMenu()
}

watch(modelState, (val) => {
    if (Array.isArray(val)) {
        const value = val.map(d => d ? formatDate(d) : '')
        emit('update:modelValue', (value as InputDatePickerModel))
        return
    }
    if (val !== modelValue.value && typeof val === 'string') {
        emit('update:modelValue', formatDate(val))
    }
})

watch(modelValue, (val) => {
    if (Array.isArray(val) && !equals(val, modelState.value)) {
        modelState.value = val.map(d => formatDate(d)) as InputDatePickerModel
    }
    if (val !== modelState.value && typeof val === 'string') {
        modelState.value = formatDate(val)
    }
})

</script>

<style lang="sass" scoped>
.tl-datepicker
    overflow: visible
    position: relative
    &.dense
        min-height: 45px
    .label-input-wrapper
        cursor: pointer
        position: relative
        .tl-datepicker-calendar-trigger
            position: absolute
            right: 0px
            top: 0px
            width: 30px
            height: 100%
            display: flex
            justify-content: flex-start
            align-items: center
        .value
            padding-top: 0px
            padding-right: 35px
        &.smallLabel
            .value
                padding-top: 10px
    .action-button
        width: 100%
</style>
